/**
 * API Service para Logs de Atividade das OS
 * Serviços para consumir APIs da Fase 4.3
 * 
 * @author Equipe GestaoeCRM
 * @date 02/10/2025
 * @version 1.0.0
 */

import api from './api';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export type OSActivityType = 
  | 'CREATED'
  | 'UPDATED' 
  | 'STARTED'
  | 'PAUSED'
  | 'RESUMED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'STATUS_CHANGED'
  | 'ASSIGNED'
  | 'UNASSIGNED'
  | 'COMMENT_ADDED'
  | 'FILE_ATTACHED'
  | 'CHECKLIST_UPDATED';

export interface OSActivityLog {
  id: string;
  tenantId: string;
  orderOfServiceId: string;
  userId: string;
  action: OSActivityType;
  description: string;
  metadata?: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  
  // Relations
  user?: {
    id: string;
    name: string;
    email: string;
  };
  orderOfService?: {
    id: string;
    title: string;
  };
}

export interface ActivityStatsResponse {
  success: boolean;
  data: {
    totalActivities: number;
    activitiesByAction: Record<OSActivityType, number>;
    activitiesByUser: Array<{
      userId: string;
      userName: string;
      count: number;
    }>;
    dailyActivity: Array<{
      date: string;
      count: number;
    }>;
    topUsers: Array<{
      userId: string;
      userName: string;
      count: number;
    }>;
  };
}

export interface ListActivityLogsParams {
  orderOfServiceId?: string;
  userId?: string;
  action?: OSActivityType;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface ListActivityLogsResponse {
  success: boolean;
  data: OSActivityLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================================================
// FUNÇÕES DE API
// ============================================================================

/**
 * Lista logs de atividade de uma OS específica
 */
export const getOSLogs = async (osId: string): Promise<OSActivityLog[]> => {
  const response = await api.get(`/os/${osId}/logs`);
  return response.data.data;
};

/**
 * Lista todos os logs de atividade com filtros
 */
export const listActivityLogs = async (params?: ListActivityLogsParams): Promise<ListActivityLogsResponse> => {
  const response = await api.get('/activity-logs', { params });
  return response.data;
};

/**
 * Obtém estatísticas de atividade
 */
export const getActivityStats = async (orderOfServiceId?: string): Promise<ActivityStatsResponse> => {
  const params = orderOfServiceId ? { orderOfServiceId } : {};
  const response = await api.get('/activity-logs/stats', { params });
  return response.data;
};

/**
 * Obtém atividade de um usuário específico
 */
export const getUserActivity = async (userId: string, page = 1, limit = 10): Promise<ListActivityLogsResponse> => {
  const response = await api.get(`/users/${userId}/activity`, {
    params: { page, limit }
  });
  return response.data;
};

/**
 * Obtém atividade recente para dashboard
 */
export const getRecentActivity = async (): Promise<OSActivityLog[]> => {
  const response = await api.get('/dashboard/recent-activity');
  return response.data.data;
};

/**
 * Obtém relatório de atividades agrupado por área, categoria e status
 */
export const getActivityReportByArea = async (): Promise<any> => {
  const response = await api.get('/activity-logs/report-by-area');
  return response.data;
};

/**
 * Obtém relatório de produtividade agrupado por categoria e tipo de ação
 */
export const getProductivityReport = async (): Promise<any> => {
  const response = await api.get('/activity-logs/productivity-report');
  return response.data;
};

// ============================================================================
// HELPERS E FORMATAÇÃO
// ============================================================================

/**
 * Mapeia ações para labels em português
 */
export const getActionLabel = (action: OSActivityType): string => {
  const labels: Record<OSActivityType, string> = {
    CREATED: 'Criada',
    UPDATED: 'Atualizada',
    STARTED: 'Iniciada',
    PAUSED: 'Pausada',
    RESUMED: 'Retomada',
    COMPLETED: 'Concluída',
    CANCELLED: 'Cancelada',
    STATUS_CHANGED: 'Status Alterado',
    ASSIGNED: 'Atribuída',
    UNASSIGNED: 'Desatribuída',
    COMMENT_ADDED: 'Comentário Adicionado',
    FILE_ATTACHED: 'Arquivo Anexado',
    CHECKLIST_UPDATED: 'Checklist Atualizado',
  };
  
  return labels[action] || action;
};

/**
 * Mapeia ações para cores
 */
export const getActionColor = (action: OSActivityType): string => {
  const colors: Record<OSActivityType, string> = {
    CREATED: 'bg-blue-100 text-blue-800',
    UPDATED: 'bg-yellow-100 text-yellow-800',
    STARTED: 'bg-green-100 text-green-800',
    PAUSED: 'bg-orange-100 text-orange-800',
    RESUMED: 'bg-green-100 text-green-800',
    COMPLETED: 'bg-purple-100 text-purple-800',
    CANCELLED: 'bg-red-100 text-red-800',
    STATUS_CHANGED: 'bg-indigo-100 text-indigo-800',
    ASSIGNED: 'bg-cyan-100 text-cyan-800',
    UNASSIGNED: 'bg-gray-100 text-gray-800',
    COMMENT_ADDED: 'bg-teal-100 text-teal-800',
    FILE_ATTACHED: 'bg-pink-100 text-pink-800',
    CHECKLIST_UPDATED: 'bg-violet-100 text-violet-800',
  };
  
  return colors[action] || 'bg-gray-100 text-gray-800';
};

/**
 * Mapeia ações para ícones (Lucide React)
 */
export const getActionIcon = (action: OSActivityType): string => {
  const icons: Record<OSActivityType, string> = {
    CREATED: 'Plus',
    UPDATED: 'Edit',
    STARTED: 'Play',
    PAUSED: 'Pause',
    RESUMED: 'Play',
    COMPLETED: 'CheckCircle',
    CANCELLED: 'XCircle',
    STATUS_CHANGED: 'RefreshCw',
    ASSIGNED: 'UserPlus',
    UNASSIGNED: 'UserMinus',
    COMMENT_ADDED: 'MessageSquare',
    FILE_ATTACHED: 'Paperclip',
    CHECKLIST_UPDATED: 'CheckSquare',
  };
  
  return icons[action] || 'Activity';
};

/**
 * Formata data para exibição na timeline
 */
export const formatActivityDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) {
    return 'Agora mesmo';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} min atrás`;
  } else if (diffInMinutes < 1440) { // 24 horas
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours}h atrás`;
  } else {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

/**
 * React Query Keys para cache
 */
export const osActivityLogKeys = {
  all: ['osActivityLogs'] as const,
  lists: () => [...osActivityLogKeys.all, 'list'] as const,
  list: (filters: ListActivityLogsParams) => [...osActivityLogKeys.lists(), filters] as const,
  osLogs: (osId: string) => [...osActivityLogKeys.all, 'osLogs', osId] as const,
  stats: (orderOfServiceId?: string) => [...osActivityLogKeys.all, 'stats', orderOfServiceId] as const,
  userActivity: (userId: string) => [...osActivityLogKeys.all, 'userActivity', userId] as const,
  recentActivity: () => [...osActivityLogKeys.all, 'recentActivity'] as const,
};